import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  GripHorizontal,
  Lightbulb,
  Map,
  Minus,
  Sparkles,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import { runFeaturePrepare } from "../../lib/contextualTourPages";
import { useTour } from "./TourProvider";
import "./tour.css";

const MODAL_WIDTH = 320;
const MODAL_HEIGHT_ESTIMATE = 300;
const ANCHOR_GAP = 14;
const TOP_BAR_OFFSET = 60;
const ARROW_INSET = 36;
const ARROW_SIZE = 12;
const VIEWPORT_PAD = 12;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const queryTargets = (selector) => {
  if (!selector) return [];

  try {
    return Array.from(document.querySelectorAll(selector));
  } catch {
    return selector
      .split(",")
      .flatMap((part) => {
        try {
          return Array.from(document.querySelectorAll(part.trim()));
        } catch {
          return [];
        }
      });
  }
};

const clampPosition = (position) => {
  if (typeof window === "undefined") return position;

  return {
    x: Math.min(Math.max(12, position.x), Math.max(12, window.innerWidth - MODAL_WIDTH - 12)),
    y: Math.min(Math.max(64, position.y), Math.max(64, window.innerHeight - 120)),
  };
};

const getContentBounds = () => {
  if (typeof window === "undefined") {
    return { left: VIEWPORT_PAD, right: window?.innerWidth || 1280 };
  }

  const sidebar = document.querySelector("[data-sidebar]");
  if (sidebar instanceof HTMLElement) {
    const rect = sidebar.getBoundingClientRect();
    return {
      left: rect.right + VIEWPORT_PAD,
      right: window.innerWidth - VIEWPORT_PAD,
    };
  }

  return {
    left: VIEWPORT_PAD,
    right: window.innerWidth - VIEWPORT_PAD,
  };
};

const clampPlacementX = (x, width, bounds) =>
  clamp(x, bounds.left, bounds.right - width);

const getHorizontalBounds = (strategy, contentBounds) => {
  if (strategy === "beside-left") {
    return {
      left: VIEWPORT_PAD,
      right: contentBounds.right,
    };
  }

  return contentBounds;
};

const clampXInContent = (x, width, contentBounds) =>
  clampPlacementX(x, width, contentBounds);

const modalBox = (x, y, width, height) => ({
  left: x,
  top: y,
  right: x + width,
  bottom: y + height,
});

const rectsOverlap = (a, b, gap = 12) =>
  !(
    a.right + gap <= b.left ||
    a.left >= b.right + gap ||
    a.bottom + gap <= b.top ||
    a.top >= b.bottom + gap
  );

const pickPrimaryTarget = (targets) => {
  let best = null;
  let smallestArea = Infinity;

  for (const element of targets) {
    if (!(element instanceof HTMLElement)) continue;
    const { width, height } = element.getBoundingClientRect();
    const area = width * height;
    if (area > 0 && area < smallestArea) {
      smallestArea = area;
      best = element;
    }
  }

  return best || targets[0];
};

const getTargetAimY = (targetRect) => {
  const isTall = targetRect.height > 220;
  const aim = isTall
    ? targetRect.top + Math.min(64, targetRect.height * 0.15)
    : targetRect.top + targetRect.height / 2;
  return clamp(aim, targetRect.top + 8, targetRect.bottom - 8);
};

const getTargetAimX = (targetRect) => {
  const centerX = targetRect.left + targetRect.width / 2;
  return clamp(centerX, targetRect.left + 8, targetRect.right - 8);
};

const buildSideArrowOffset = (aimY, modalY, modalHeight) =>
  clamp(aimY - modalY, ARROW_INSET, modalHeight - ARROW_INSET);

const buildVerticalArrowOffset = (aimX, modalX, modalWidth) =>
  clamp(aimX - modalX, ARROW_INSET, modalWidth - ARROW_INSET);

const classifyTarget = (targetRect, viewportWidth, viewportHeight) => {
  const aspect = targetRect.width / Math.max(targetRect.height, 1);
  const areaRatio =
    (targetRect.width * targetRect.height) / (viewportWidth * viewportHeight);

  if (targetRect.height <= 96 && aspect >= 2.4) return "ribbon";
  if (areaRatio < 0.06 && targetRect.height < 220) return "compact";
  if (targetRect.height >= 220 || areaRatio >= 0.14) return "block";
  return "panel";
};

const buildPlacement = ({
  x,
  y,
  arrowSide,
  width,
  height,
  horizontalBounds,
  maxY,
}) => ({
  x: clampPlacementX(x, width, horizontalBounds),
  y: clamp(y, TOP_BAR_OFFSET, maxY),
  arrowSide,
});

const targetBoxFromRect = (targetRect) =>
  modalBox(
    targetRect.left,
    targetRect.top,
    targetRect.width,
    targetRect.height
  );

const placementOverlapsTarget = (placement, width, height, targetRect, gap = 12) => {
  if (!placement) return true;
  return rectsOverlap(
    modalBox(placement.x, placement.y, width, height),
    targetBoxFromRect(targetRect),
    gap
  );
};

const placementFits = (
  placement,
  width,
  height,
  targetRect,
  horizontalBounds,
  maxY
) => {
  const box = modalBox(placement.x, placement.y, width, height);

  return (
    box.left >= horizontalBounds.left - 1 &&
    box.right <= horizontalBounds.right + 1 &&
    box.top >= TOP_BAR_OFFSET - 1 &&
    box.bottom <= maxY + height + VIEWPORT_PAD &&
    !placementOverlapsTarget(placement, width, height, targetRect)
  );
};

const getSideSpace = (targetRect, width, height, contentBounds, sideY) => {
  const relaxedLeftBounds = getHorizontalBounds("beside-left", contentBounds);
  const besideLeftX = targetRect.left - ANCHOR_GAP - width;
  const besideRightX = targetRect.right + ANCHOR_GAP;
  const clampedBesideRightX = clampPlacementX(
    besideRightX,
    width,
    contentBounds
  );
  const relaxedBesideLeft = buildPlacement({
    x: besideLeftX,
    y: sideY,
    arrowSide: "right",
    width,
    height,
    horizontalBounds: relaxedLeftBounds,
    maxY: sideY,
  });

  return {
    spaceLeft: targetRect.left - VIEWPORT_PAD,
    spaceRight: contentBounds.right - targetRect.right,
    canPlaceBesideLeft: placementFits(
      relaxedBesideLeft,
      width,
      height,
      targetRect,
      relaxedLeftBounds,
      sideY
    ),
    canPlaceBesideRight: besideRightX + width <= contentBounds.right + 1,
    clampedBesideRightOverlaps:
      clampedBesideRightX < besideRightX - 4 &&
      placementOverlapsTarget(
        { x: clampedBesideRightX, y: sideY, arrowSide: "left" },
        width,
        height,
        targetRect
      ),
  };
};

const shouldPreferBesideLeft = (
  targetRect,
  width,
  contentBounds,
  targetOnRight,
  sideY,
  height
) => {
  if (targetOnRight) return false;

  const {
    spaceLeft,
    spaceRight,
    canPlaceBesideLeft,
    canPlaceBesideRight,
    clampedBesideRightOverlaps,
  } = getSideSpace(targetRect, width, height, contentBounds, sideY);

  if (!canPlaceBesideLeft) return false;

  const besideRightPlacement = buildPlacement({
    x: targetRect.right + ANCHOR_GAP,
    y: sideY,
    arrowSide: "left",
    width,
    height,
    horizontalBounds: contentBounds,
    maxY: sideY,
  });
  const besideRightOverlaps = placementOverlapsTarget(
    besideRightPlacement,
    width,
    height,
    targetRect
  );

  if (besideRightOverlaps || clampedBesideRightOverlaps) return true;

  const rightIsTight =
    !canPlaceBesideRight || spaceRight < width + ANCHOR_GAP;
  const leftHasRoom = spaceLeft >= width + ANCHOR_GAP * 0.45;

  return leftHasRoom && (rightIsTight || spaceLeft >= spaceRight);
};

const buildStrategyOrder = ({
  targetClass,
  targetOnRight,
  targetInLowerThird,
  preferBesideLeft,
}) => {
  const besideOnly = preferBesideLeft
    ? ["beside-left", "beside-right", "below-aligned", "above-aligned"]
    : ["beside-right", "beside-left", "below-aligned", "above-aligned"];

  if (targetClass === "ribbon") {
    return preferBesideLeft
      ? ["beside-left", "beside-right", "below-aligned", "above-aligned"]
      : ["beside-right", "beside-left", "below-aligned", "above-aligned"];
  }

  if (targetClass === "compact") {
    return targetOnRight
      ? ["beside-left", "beside-right", "below-aligned", "above-aligned"]
      : besideOnly;
  }

  if (targetClass === "block") {
    if (targetInLowerThird) {
      return preferBesideLeft
        ? ["above-aligned", "beside-left", "beside-right", "below-aligned"]
        : ["above-aligned", "beside-right", "beside-left", "below-aligned"];
    }
    return preferBesideLeft
      ? ["beside-left", "below-aligned", "above-aligned", "beside-right"]
      : ["beside-right", "below-aligned", "above-aligned", "beside-left"];
  }

  return targetOnRight
    ? ["beside-left", "below-aligned", "above-aligned", "beside-right"]
    : preferBesideLeft
      ? ["beside-left", "below-aligned", "above-aligned", "beside-right"]
      : ["beside-right", "below-aligned", "above-aligned", "beside-left"];
};

const computeAnchoredPosition = (
  targetRect,
  modalWidth = MODAL_WIDTH,
  modalHeight = MODAL_HEIGHT_ESTIMATE
) => {
  const width = Math.min(modalWidth, window.innerWidth - 32);
  const height = modalHeight;
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const contentBounds = getContentBounds();
  const aimY = getTargetAimY(targetRect);
  const aimX = getTargetAimX(targetRect);
  const maxY = viewportHeight - height - VIEWPORT_PAD;
  const targetClass = classifyTarget(targetRect, viewportWidth, viewportHeight);
  const targetOnRight = targetRect.left > viewportWidth * 0.56;
  const targetInLowerThird = targetRect.top > viewportHeight * 0.58;

  const besideRightX = targetRect.right + ANCHOR_GAP;
  const besideLeftX = targetRect.left - ANCHOR_GAP - width;
  const belowY = targetRect.bottom + ANCHOR_GAP;
  const aboveY = targetRect.top - ANCHOR_GAP - height;
  const centerOnTargetX = clampXInContent(
    targetRect.left + targetRect.width / 2 - width / 2,
    width,
    contentBounds
  );
  const alignedBelowX = centerOnTargetX;
  const alignedAboveX = centerOnTargetX;
  const sideY = clamp(aimY - height / 2, TOP_BAR_OFFSET, maxY);
  const preferBesideLeft = shouldPreferBesideLeft(
    targetRect,
    width,
    contentBounds,
    targetOnRight,
    sideY,
    height
  );

  const makeCandidate = (strategy) => {
    const horizontalBounds = getHorizontalBounds(strategy, contentBounds);

    switch (strategy) {
      case "beside-right":
        return buildPlacement({
          x: besideRightX,
          y: sideY,
          arrowSide: "left",
          width,
          height,
          horizontalBounds,
          maxY,
        });
      case "beside-left":
        return buildPlacement({
          x: besideLeftX,
          y: sideY,
          arrowSide: "right",
          width,
          height,
          horizontalBounds,
          maxY,
        });
      case "below-aligned":
        return buildPlacement({
          x: alignedBelowX,
          y: belowY,
          arrowSide: "top",
          width,
          height,
          horizontalBounds,
          maxY,
        });
      case "above-aligned":
        return buildPlacement({
          x: alignedAboveX,
          y: aboveY,
          arrowSide: "bottom",
          width,
          height,
          horizontalBounds,
          maxY,
        });
      default:
        return null;
    }
  };

  const placementFitsStrategy = (strategy, placement) =>
    placement &&
    placementFits(
      placement,
      width,
      height,
      targetRect,
      getHorizontalBounds(strategy, contentBounds),
      maxY
    );

  const orderedStrategies = buildStrategyOrder({
    targetClass,
    targetOnRight,
    targetInLowerThird,
    preferBesideLeft,
  });
  const candidates = orderedStrategies
    .map((strategy) => ({ strategy, placement: makeCandidate(strategy) }))
    .filter(({ strategy, placement }) =>
      placementFitsStrategy(strategy, placement)
    );

  const pickFirstFit = (strategies) =>
    strategies
      .map((strategy) => ({ strategy, placement: makeCandidate(strategy) }))
      .find(({ strategy, placement }) =>
        placementFitsStrategy(strategy, placement)
      ) || null;

  let chosenEntry = candidates[0] || null;

  if (!chosenEntry) {
    const fallbackStrategies = preferBesideLeft
      ? ["beside-left", "beside-right", "below-aligned", "above-aligned"]
      : ["beside-right", "beside-left", "below-aligned", "above-aligned"];
    chosenEntry = pickFirstFit(fallbackStrategies);
  }

  if (!chosenEntry) {
    const rescueStrategies = preferBesideLeft
      ? ["beside-left", "below-aligned", "above-aligned", "beside-right"]
      : ["beside-left", "beside-right", "below-aligned", "above-aligned"];
    chosenEntry =
      pickFirstFit(rescueStrategies) || {
        strategy: preferBesideLeft ? "beside-left" : "beside-right",
        placement: buildPlacement({
          x: preferBesideLeft ? besideLeftX : besideRightX,
          y: sideY,
          arrowSide: preferBesideLeft ? "right" : "left",
          width,
          height,
          horizontalBounds: getHorizontalBounds(
            preferBesideLeft ? "beside-left" : "beside-right",
            contentBounds
          ),
          maxY,
        }),
      };
  }

  let chosen = chosenEntry.placement;

  let { x, y, arrowSide } = chosen;

  if (arrowSide === "left" || arrowSide === "right") {
    let arrowOffset = buildSideArrowOffset(aimY, y, height);
    y = clamp(aimY - arrowOffset, TOP_BAR_OFFSET, maxY);
    return {
      x,
      y,
      arrowSide,
      arrowOffset: buildSideArrowOffset(aimY, y, height),
      modalWidth: width,
    };
  }

  return {
    x,
    y,
    arrowSide,
    arrowOffset: buildVerticalArrowOffset(aimX, x, width),
    modalWidth: width,
  };
};

const GuidedTourTopBar = ({
  sectionTitle,
  pageTitle,
  pageIndex,
  pageTotal,
  canGoBack,
  canGoNext,
  onBack,
  onNext,
  onExit,
  onSkip,
}) => (
  <div className="tour-status-bar">
    <div className="flex items-center gap-2 min-w-0">
      <Map className="w-4 h-4 text-emerald-400 shrink-0" />
      <div className="min-w-0">
        <p className="tour-status-bar__title truncate">Guided Tour Active</p>
        <p className="tour-status-bar__subtitle truncate">
          {sectionTitle ? `${sectionTitle} · ` : ""}
          {pageTitle}
          {pageTotal > 0 ? ` · Page ${pageIndex} of ${pageTotal}` : ""}
        </p>
      </div>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white disabled:opacity-35"
        onClick={onBack}
        disabled={!canGoBack}
      >
        <ChevronLeft className="w-4 h-4 mr-1" />
        Previous Page
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white disabled:opacity-35"
        onClick={onNext}
        disabled={!canGoNext}
      >
        Next Page
        <ChevronRight className="w-4 h-4 ml-1" />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="border-slate-600 bg-slate-800 text-white hover:bg-slate-700"
        onClick={onSkip}
      >
        Skip Tour
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-white hover:bg-slate-800"
        onClick={onExit}
      >
        <X className="w-4 h-4 mr-1" />
        Exit
      </Button>
    </div>
  </div>
);

export default function FloatingTourAssistant() {
  const {
    currentRoute,
    canGoBackFocus,
    canGoBackPage,
    canGoNextFocus,
    canGoNextPage,
    goBackPage,
    goBackFocus,
    goNextPage,
    goNextFocus,
    pageConfig,
    platformSteps,
    setMinimized,
    setPanelPosition,
    stopTour,
    skipTour,
    tour,
    tourFeatures,
  } = useTour();

  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const userDraggedRef = useRef(false);
  const lastTargetRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [highlightLabel, setHighlightLabel] = useState(null);
  const [highlightMessage, setHighlightMessage] = useState("");
  const [anchorLayout, setAnchorLayout] = useState({
    x: 16,
    y: 96,
    arrowSide: "left",
    arrowOffset: 120,
    modalWidth: MODAL_WIDTH,
    ready: false,
  });

  const updateAnchorFromTarget = (target) => {
    if (!target || userDraggedRef.current) return;

    const targetRect =
      target instanceof HTMLElement ? target.getBoundingClientRect() : target;
    if (!targetRect || (targetRect.width <= 0 && targetRect.height <= 0)) return;

    const panel = panelRef.current;
    const modalWidth =
      panel?.offsetWidth || Math.min(MODAL_WIDTH, window.innerWidth - 32);
    const modalHeight = panel?.offsetHeight || MODAL_HEIGHT_ESTIMATE;

    setAnchorLayout({
      ...computeAnchoredPosition(targetRect, modalWidth, modalHeight),
      ready: true,
    });
  };

  const features = tourFeatures?.length ? tourFeatures : pageConfig.features || [];
  const selectedFeatureIndex = Math.min(
    tour.selectedFeatureIndex || 0,
    Math.max(features.length - 1, 0)
  );
  const selectedFeature = features[selectedFeatureIndex];

  const manualPosition = tour.panelPosition
    ? clampPosition(tour.panelPosition)
    : null;

  const position = manualPosition || {
    x: anchorLayout.x,
    y: anchorLayout.y,
  };

  const showAnchorArrow = manualPosition == null && anchorLayout.ready;
  const arrowHalf = ARROW_SIZE / 2;
  const arrowStyle = showAnchorArrow
    ? {
        width: ARROW_SIZE,
        height: ARROW_SIZE,
        ...(anchorLayout.arrowSide === "left" && {
          left: -arrowHalf,
          top: anchorLayout.arrowOffset - arrowHalf,
        }),
        ...(anchorLayout.arrowSide === "right" && {
          right: -arrowHalf,
          top: anchorLayout.arrowOffset - arrowHalf,
        }),
        ...(anchorLayout.arrowSide === "top" && {
          top: -arrowHalf,
          left: anchorLayout.arrowOffset - arrowHalf,
        }),
        ...(anchorLayout.arrowSide === "bottom" && {
          bottom: -arrowHalf,
          left: anchorLayout.arrowOffset - arrowHalf,
        }),
      }
    : undefined;

  const currentPlatformStep = platformSteps[tour.platformStepIndex];
  const platformLabel = currentPlatformStep?.label || pageConfig.title;
  const platformSection = currentPlatformStep?.section;
  const pageIndex = (tour.platformStepIndex || 0) + 1;
  const pageTotal = platformSteps.length;

  const platformProgress = useMemo(() => {
    if (!pageTotal) return 0;
    return ((tour.platformStepIndex || 0) + 1) / pageTotal;
  }, [pageTotal, tour.platformStepIndex]);

  useEffect(() => {
    if (selectedFeature?.prepare) {
      runFeaturePrepare(selectedFeature.prepare);
    }
  }, [selectedFeature?.prepare, selectedFeatureIndex, currentRoute]);

  useEffect(() => {
    userDraggedRef.current = false;
    lastTargetRef.current = null;
    setPanelPosition(null);
    setAnchorLayout((current) => ({ ...current, ready: false }));
  }, [selectedFeatureIndex, currentRoute, setPanelPosition]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || !tour.active || tour.minimized) return undefined;

    const observer = new ResizeObserver(() => {
      if (lastTargetRef.current && !userDraggedRef.current) {
        updateAnchorFromTarget(lastTargetRef.current);
      }
    });

    observer.observe(panel);
    return () => observer.disconnect();
  }, [tour.active, tour.minimized, selectedFeatureIndex]);

  useEffect(() => {
    let frameId = null;

    const clearHighlights = () => {
      document
        .querySelectorAll(".tour-highlight-ring")
        .forEach((element) => element.classList.remove("tour-highlight-ring"));
      setHighlightLabel(null);
    };

    const syncHighlight = (shouldScroll = false, showNotFoundError = false) => {
      if (!tour.active || tour.minimized || !selectedFeature?.selector) {
        clearHighlights();
        return false;
      }

      const targets = queryTargets(selectedFeature.selector).filter(
        (element) => element instanceof HTMLElement
      );

      document
        .querySelectorAll(".tour-highlight-ring")
        .forEach((element) => element.classList.remove("tour-highlight-ring"));

      if (targets.length === 0) {
        setHighlightLabel(null);
        if (showNotFoundError) {
          setHighlightMessage(
            `(Add data to see this feature in action)`
          );
          if (!userDraggedRef.current) {
            setAnchorLayout({
              x: typeof window !== "undefined" ? window.innerWidth / 2 - (MODAL_WIDTH / 2) : 16,
              y: typeof window !== "undefined" ? window.innerHeight / 2 - 150 : 96,
              arrowSide: "none",
              arrowOffset: 0,
              modalWidth: MODAL_WIDTH,
              ready: true,
            });
          }
        }
        return false;
      }

      const primaryTarget = pickPrimaryTarget(targets);
      primaryTarget.classList.add("tour-highlight-ring");

      if (shouldScroll) {
        primaryTarget.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }

      const rect = primaryTarget.getBoundingClientRect();
      lastTargetRef.current = primaryTarget;

      if (!userDraggedRef.current) {
        updateAnchorFromTarget(primaryTarget);
        requestAnimationFrame(() => {
          updateAnchorFromTarget(primaryTarget);
        });
      }

      const labelWidth = Math.min(260, Math.max(180, selectedFeature.title.length * 8 + 28));
      const labelX = clamp(rect.left, 16, Math.max(16, window.innerWidth - labelWidth - 16));
      const preferredY = rect.top - 38;
      const fallbackY = rect.bottom + 10;
      const labelY =
        preferredY >= TOP_BAR_OFFSET + 8
          ? preferredY
          : clamp(
              fallbackY,
              TOP_BAR_OFFSET + 8,
              Math.max(TOP_BAR_OFFSET + 8, window.innerHeight - 48)
            );

      setHighlightLabel({
        title: selectedFeature.title,
        x: labelX,
        y: labelY,
      });
      setHighlightMessage("");
      return true;
    };

    const scheduleSync = () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => syncHighlight(false));
    };

    if (!tour.active || tour.minimized || !selectedFeature?.selector) {
      setHighlightLabel(null);
      return undefined;
    }

    const retryDelays = [120, 400, 800, 1200];
    const retryTimers = retryDelays.map((delay, index) =>
      window.setTimeout(() => {
        syncHighlight(
          index === 0,
          index === retryDelays.length - 1
        );
      }, delay)
    );

    window.addEventListener("scroll", scheduleSync, true);
    window.addEventListener("resize", scheduleSync);

    return () => {
      retryTimers.forEach((id) => window.clearTimeout(id));
      if (frameId) window.cancelAnimationFrame(frameId);
      window.removeEventListener("scroll", scheduleSync, true);
      window.removeEventListener("resize", scheduleSync);
      clearHighlights();
    };
  }, [
    currentRoute,
    selectedFeature?.selector,
    selectedFeature?.title,
    tour.active,
    tour.minimized,
    selectedFeatureIndex,
  ]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!dragRef.current) return;

      userDraggedRef.current = true;
      setPanelPosition(
        clampPosition({
          x: event.clientX - dragRef.current.offsetX,
          y: event.clientY - dragRef.current.offsetY,
        })
      );
    };

    const handleMouseUp = () => {
      dragRef.current = null;
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, setPanelPosition]);

  useEffect(() => {
    if (tour.active) {
      document.body.classList.add("tour-active");
    } else {
      document.body.classList.remove("tour-active");
    }
    return () => document.body.classList.remove("tour-active");
  }, [tour.active]);

  useEffect(() => {
    if (!tour.active) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") stopTour();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tour.active, stopTour]);

  useEffect(() => {
    if (!tour.active || tour.minimized) return undefined;
    const handleKeyDown = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      if (e.key === "ArrowRight" && canGoNextFocus) {
        e.preventDefault();
        setHighlightMessage("");
        goNextFocus();
      } else if (e.key === "ArrowLeft" && canGoBackFocus) {
        e.preventDefault();
        setHighlightMessage("");
        goBackFocus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [tour.active, tour.minimized, canGoNextFocus, canGoBackFocus, goNextFocus, goBackFocus]);

  if (!tour.active) return null;

  const beginDrag = (event) => {
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;

    dragRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    setIsDragging(true);
  };

  const showPreviousFeature = () => {
    setHighlightMessage("");
    goBackFocus();
  };

  const showNextFeature = () => {
    setHighlightMessage("");
    goNextFocus();
  };

  const topBar = (
    <GuidedTourTopBar
      sectionTitle={platformSection}
      pageTitle={platformLabel}
      pageIndex={pageIndex}
      pageTotal={pageTotal}
      canGoBack={canGoBackPage}
      canGoNext={canGoNextPage}
      onBack={goBackPage}
      onNext={goNextPage}
      onExit={stopTour}
      onSkip={skipTour}
    />
  );

  if (tour.minimized) {
    return (
      <>
        {topBar}
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="fixed bottom-5 right-5 z-[10000] flex items-center gap-2 rounded-md border border-emerald-200 bg-white px-3 py-2 text-sm font-semibold text-emerald-800 shadow-xl transition hover:bg-emerald-50"
        >
          <Sparkles className="h-4 w-4" />
          Guided Tour
        </button>
      </>
    );
  }

  return (
    <>
      {topBar}

      <aside
        ref={panelRef}
        className="guided-tour-modal text-slate-900"
        style={{
          left: position.x,
          top: position.y,
          width: manualPosition ? undefined : anchorLayout.modalWidth,
        }}
        aria-label="Guided tour assistant"
      >
        {showAnchorArrow ? (
          <span
            className={`guided-tour-arrow guided-tour-arrow--${anchorLayout.arrowSide}`}
            style={arrowStyle}
            aria-hidden="true"
          />
        ) : null}
        <div
          className="guided-tour-modal__header cursor-move select-none"
          onMouseDown={beginDrag}
        >
          <div className="flex items-center gap-2">
            <Map className="h-4 w-4 text-emerald-400 shrink-0" />
            <div>
              <p className="text-[13px] font-semibold leading-tight">Guided Tour</p>
              <p className="text-[11px] text-slate-300">
                Now touring {pageConfig.title}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-0.5">
            <GripHorizontal className="h-3.5 w-3.5 text-slate-500" />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => setMinimized(true)}
              className="h-7 w-7 text-slate-300 hover:bg-slate-700 hover:text-white"
              aria-label="Minimize tour"
            >
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={skipTour}
              className="h-7 px-2 text-xs font-semibold text-slate-300 hover:bg-slate-700 hover:text-white"
            >
              Skip Tour
            </Button>
          </div>
        </div>

        <div className="guided-tour-modal__body">
          <div>
            <div className="guided-tour-modal__page-meta">
              <span className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 border border-slate-200">
                <Map className="h-3 w-3" />
                Current page
              </span>
              <span className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold tabular-nums text-slate-700">
                {pageTotal > 0 ? `${pageIndex} of ${pageTotal}` : "—"}
              </span>
            </div>
            <h2 className="guided-tour-modal__page-title">{pageConfig.title}</h2>
            <p className="guided-tour-modal__page-description line-clamp-2">
              {pageConfig.description}
            </p>
          </div>

          {selectedFeature && (
            <div className="guided-tour-modal__focus-card">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="guided-tour-modal__section-label guided-tour-modal__section-label--focus flex items-center gap-1 text-blue-600">
                  <Lightbulb className="w-4 h-4" />
                  Focus Area
                </span>
              </div>
              <h3 className="text-sm font-semibold text-slate-900">
                {selectedFeature.title}
              </h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-slate-700">
                {selectedFeature.description}
              </p>
              {highlightMessage && (
                <p className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {highlightMessage}
                </p>
              )}
              <div className="mt-3 pt-3 border-t border-blue-100 flex items-center justify-between">
                <span className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">
                  Step {selectedFeatureIndex + 1} of {features.length}
                </span>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 px-3 text-xs font-semibold text-slate-600 border-slate-300 hover:bg-slate-50"
                    disabled={!canGoBackFocus}
                    onClick={showPreviousFeature}
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 px-4 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-500/20"
                    disabled={!canGoNextFocus}
                    onClick={showNextFeature}
                  >
                    Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="guided-tour-modal__product-tour">
            <div className="flex items-center justify-between mb-2">
              <span className="guided-tour-modal__section-label">Overall Progress</span>
              <span className="text-[10px] font-bold text-slate-500">{Math.round(platformProgress * 100)}%</span>
            </div>
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all duration-500 ease-out"
                style={{ width: `${platformProgress * 100}%` }}
              />
            </div>
            <div className="mt-3 text-center">
              <button 
                onClick={skipTour} 
                className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 transition-colors underline underline-offset-2"
              >
                Skip Tour & Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </aside>

      {highlightLabel && (
        <div
          className="tour-highlight-pill"
          style={{ left: highlightLabel.x, top: highlightLabel.y }}
        >
          {highlightLabel.title}
        </div>
      )}
    </>
  );
}
